import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

/**
 * Admin usage/analytics tab hooks (DUE-173 C5).
 *
 * Schemas mirror the live FastAPI shapes in
 * `src/api/schemas/admin_analytics.py`:
 *   - FeatureUsageRowDTO         — GET /api/admin/analytics/feature-usage
 *   - TopFeatureRowDTO           — GET /api/admin/analytics/top-features
 *   - ActivityResponseDTO        — GET /api/admin/analytics/activity
 *   - RetentionCohortDTO         — GET /api/admin/analytics/retention
 *   - FunnelStepDTO              — GET /api/admin/analytics/funnel
 *   - PayerCorrelationRowDTO     — GET /api/admin/analytics/payer-correlation
 *   - PairSectionsDTO            — GET /api/admin/analytics/sections
 *   - PartnerDayLoopDTO          — GET /api/admin/analytics/partner-day-loop
 *   - PlatformRetentionRowDTO    — GET /api/admin/analytics/platform-retention
 *   - WebAuthFunnelDTO           — GET /api/admin/analytics/web-auth-funnel
 *   - ReadingDiversityDTO        — GET /api/admin/analytics/reading-diversity
 *   - SecondSynastryDTO          — GET /api/admin/analytics/second-synastry
 *   - PaidTextRulesDTO           — GET /api/admin/analytics/paid-text-rules
 *   - TextFormsDTO               — GET /api/admin/analytics/text-forms
 *
 * The first six share `since` / `until` (ISO date, inclusive) plus an
 * optional `platform` filter (DUE-336: `funnel` and `web-auth-funnel` take
 * it too). These stay plain
 * `since`/`until` strings here rather than a `Period` enum so the hooks
 * remain thin, backend-shaped passthroughs; the Usage page resolves the
 * `PeriodPicker`'s `period` token into concrete dates before calling them.
 */

// Backend serialises Decimal as a string (e.g. "12.34") — coerce to number
// at the boundary, same convention as web/src/api/admin/economics.ts.
const decimal = z.coerce.number();

export type PlatformFilter = "telegram" | "ios" | "web";

export interface AnalyticsWindow {
  since: string;
  until: string;
  platform?: PlatformFilter;
}

export const FeatureUsageRowSchema = z.object({
  day: z.string(),
  platform: z.string().nullable(),
  feature: z.string(),
  action: z.string(),
  events_count: z.number().int().nonnegative(),
  unique_users: z.number().int().nonnegative(),
});
export const FeatureUsageListSchema = z.array(FeatureUsageRowSchema);
export type FeatureUsageRow = z.infer<typeof FeatureUsageRowSchema>;

export const TopFeatureRowSchema = z.object({
  feature: z.string(),
  events_count: z.number().int().nonnegative(),
  unique_users: z.number().int().nonnegative(),
});
export const TopFeatureListSchema = z.array(TopFeatureRowSchema);
export type TopFeatureRow = z.infer<typeof TopFeatureRowSchema>;

export const ActivityDaySchema = z.object({
  day: z.string(),
  dau: z.number().int().nonnegative(),
});
export const ActivityResponseSchema = z.object({
  days: z.array(ActivityDaySchema),
  wau: z.number().int().nonnegative(),
  mau: z.number().int().nonnegative(),
});
export type ActivityDay = z.infer<typeof ActivityDaySchema>;
export type ActivityResponse = z.infer<typeof ActivityResponseSchema>;

export const RetentionWeekCellSchema = z.object({
  week_index: z.number().int().nonnegative(),
  active_users: z.number().int().nonnegative(),
  retention_pct: decimal,
});
export const RetentionCohortSchema = z.object({
  cohort_week: z.string(),
  cohort_size: z.number().int().nonnegative(),
  weeks: z.array(RetentionWeekCellSchema),
});
export const RetentionListSchema = z.array(RetentionCohortSchema);
export type RetentionWeekCell = z.infer<typeof RetentionWeekCellSchema>;
export type RetentionCohort = z.infer<typeof RetentionCohortSchema>;

export const FunnelStepSchema = z.object({
  feature: z.string(),
  action: z.string(),
  // DUE-493: props.step for feature:action:step keys; null for plain pairs.
  step: z.string().nullable().optional(),
  users: z.number().int().nonnegative(),
  conversion_pct: decimal,
});
export const FunnelListSchema = z.array(FunnelStepSchema);
export type FunnelStep = z.infer<typeof FunnelStepSchema>;

export const PayerCorrelationRowSchema = z.object({
  feature: z.string(),
  payer_users: z.number().int().nonnegative(),
  non_payer_users: z.number().int().nonnegative(),
});
export const PayerCorrelationListSchema = z.array(PayerCorrelationRowSchema);
export type PayerCorrelationRow = z.infer<typeof PayerCorrelationRowSchema>;

// DUE-289/291 — sections of the pair readings opened via the mono switch,
// split by the lens half, plus taps on the locked LENS half by surface.
// Raw `product_events`, so history is bounded by retention_raw_analytics_days.
export const SectionViewRowSchema = z.object({
  feature: z.string(),
  section: z.string(),
  lens: z.boolean(),
  events: z.number().int().nonnegative(),
  unique_users: z.number().int().nonnegative(),
});
export const LockedTapRowSchema = z.object({
  surface: z.string(),
  events: z.number().int().nonnegative(),
  unique_users: z.number().int().nonnegative(),
});
export const PairSectionsSchema = z.object({
  sections: z.array(SectionViewRowSchema),
  locked: z.array(LockedTapRowSchema),
});
export type PairSections = z.infer<typeof PairSectionsSchema>;

// DUE-293/295 — the partner-day loop (>= 3 distinct days in the first 14),
// D7 of that cohort vs users who never opened a day, and the lens funnel.
export const PartnerDayLoopGroupSchema = z.object({
  group: z.enum(["day_viewers", "never_viewed"]),
  cohort_size: z.number().int().nonnegative(),
  d7_eligible: z.number().int().nonnegative(),
  d7_retained: z.number().int().nonnegative(),
  d7_retention_pct: decimal.nullable(),
});
export const PartnerDayLoopSchema = z.object({
  cohort_size: z.number().int().nonnegative(),
  loop_eligible: z.number().int().nonnegative(),
  loop_users: z.number().int().nonnegative(),
  loop_share_pct: decimal.nullable(),
  groups: z.array(PartnerDayLoopGroupSchema),
  lens: z.object({
    locked_tapped_synastry_users: z.number().int().nonnegative(),
    locked_tapped_day_users: z.number().int().nonnegative(),
    lens_reading_viewers: z.number().int().nonnegative(),
    lens_day_viewers: z.number().int().nonnegative(),
    relation_paid_sets: z.number().int().nonnegative(),
    // DUE-336: DUE-291/295 success ratios, in users. `labelled_users` is a
    // snapshot of owners with a labelled live partner, not windowed.
    labelled_users: z.number().int().nonnegative(),
    relation_paid_set_users: z.number().int().nonnegative(),
    locked_tapped_users: z.number().int().nonnegative(),
    lens_reading_viewed_pct: decimal.nullable(),
    lens_day_viewed_pct: decimal.nullable(),
    paid_per_locked_pct: decimal.nullable(),
    // DUE-411: distinct users who bought a relation level, and that ÷ lock-tappers.
    purchasers: z.number().int().nonnegative().default(0),
    purchasers_per_locked_pct: decimal.nullable().default(null),
    // DUE-309: thumbs-up per relation type, the free day beside the lens.
    by_relation: z
      .array(
        z.object({
          relation_type: z.string(),
          plain_votes: z.number().int().nonnegative(),
          plain_up: z.number().int().nonnegative(),
          lens_votes: z.number().int().nonnegative(),
          lens_up: z.number().int().nonnegative(),
        }),
      )
      .default([]),
  }),
});
export type PartnerDayLoop = z.infer<typeof PartnerDayLoopSchema>;

// DUE-336 (DUE-317 spec §10): D1/D7 by the platform a user started on.
export const PlatformRetentionRowSchema = z.object({
  platform: z.enum(["telegram", "ios", "web"]),
  cohort_size: z.number().int().nonnegative(),
  d1_eligible: z.number().int().nonnegative(),
  d1_retained: z.number().int().nonnegative(),
  d1_retention_pct: decimal.nullable(),
  d7_eligible: z.number().int().nonnegative(),
  d7_retained: z.number().int().nonnegative(),
  d7_retention_pct: decimal.nullable(),
});
export const PlatformRetentionListSchema = z.array(PlatformRetentionRowSchema);
export type PlatformRetentionRow = z.infer<typeof PlatformRetentionRowSchema>;

// DUE-336 (DUE-325): the keep-access funnel. Row counts, detached rows
// included — `kept` is completed as `linked` / `merged`.
export const WebAuthMethodRowSchema = z.object({
  method: z.string(),
  started: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  linked: z.number().int().nonnegative(),
  merged: z.number().int().nonnegative(),
  signed_in: z.number().int().nonnegative(),
  replaced: z.number().int().nonnegative(),
});
export const WebAuthFunnelSchema = z.object({
  prompt_shown: z.number().int().nonnegative(),
  prompt_shown_users: z.number().int().nonnegative(),
  started: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  kept: z.number().int().nonnegative(),
  merged: z.number().int().nonnegative(),
  success_pct: decimal.nullable(),
  merged_share_pct: decimal.nullable(),
  by_method: z.array(WebAuthMethodRowSchema),
});
export type WebAuthFunnel = z.infer<typeof WebAuthFunnelSchema>;

// Transit diversity spec §C: opener / lead repeat shares of the daily
// readings plus depth-viewed per push sent (the guard-rail).
export const ReadingDiversitySchema = z.object({
  transit_days: z.number().int().nonnegative(),
  transit_opener_repeats: z.number().int().nonnegative(),
  transit_opener_repeat_pct: decimal.nullable(),
  transit_lead_repeats: z.number().int().nonnegative(),
  transit_lead_repeat_pct: decimal.nullable(),
  partner_days: z.number().int().nonnegative(),
  partner_opener_repeats: z.number().int().nonnegative(),
  partner_opener_repeat_pct: decimal.nullable(),
  partner_lists_days: z.number().int().nonnegative(),
  partner_lists_repeats: z.number().int().nonnegative(),
  partner_lists_repeat_pct: decimal.nullable(),
  partner_angle_days: z.number().int().nonnegative(),
  partner_angle_repeats: z.number().int().nonnegative(),
  partner_angle_repeat_pct: decimal.nullable(),
  push_sent: z.number().int().nonnegative(),
  depth_viewed: z.number().int().nonnegative(),
  depth_per_push_pct: decimal.nullable(),
  by_mode: z.array(z.object({
    surface: z.string(),
    locale: z.string(),
    text_mode: z.string(),
    days: z.number().int().nonnegative(),
    opener_repeats: z.number().int().nonnegative(),
    opener_repeat_pct: decimal.nullable(),
    lead_repeats: z.number().int().nonnegative().nullable(),
    lead_repeat_pct: decimal.nullable(),
  })),
});
export type ReadingDiversity = z.infer<typeof ReadingDiversitySchema>;

// DUE-499 §5.3: share of paid-text generations that broke a copy rule.
export const PaidTextRulesSchema = z.object({
  operations: z.array(z.object({
    operation: z.string(),
    checked: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    failed_pct: decimal.nullable(),
    errors: z.number().int().nonnegative(),
  })),
  rules: z.array(z.object({
    operation: z.string(),
    rule: z.string(),
    readings: z.number().int().nonnegative(),
    pct: decimal.nullable(),
  })),
  locales: z.array(z.object({
    operation: z.string(),
    locale: z.string(),
    checked: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    failed_pct: decimal.nullable(),
  })),
});
export type PaidTextRules = z.infer<typeof PaidTextRulesSchema>;

// DUE-403: form-based text against LLM text, by locale. Percentages and cost
// are Decimal strings or null (shape of TextFormsDTO, regenerated in shared/ later).
const nullableBool = z.boolean().nullable();
export const TextFormsSchema = z.object({
  since: z.string(),
  until: z.string(),
  modes: z.array(z.object({
    surface: z.string(),
    locale: z.string(),
    text_mode: z.string(),
    readings: z.number().int().nonnegative(),
    stubs: z.number().int().nonnegative(),
    stub_pct: decimal.nullable(),
    slots_filled_pct: decimal.nullable(),
    relaxed_pct: decimal.nullable(),
  })),
  fallbacks: z.array(z.object({
    operation: z.string(),
    locale: z.string(),
    reason: z.string().nullable(),
    sub_reason: z.string().nullable(),
    count: z.number().int().nonnegative(),
    attempts: z.number().int().nonnegative(),
    pct: decimal.nullable(),
  })),
  votes: z.array(z.object({
    operation: z.string(),
    locale: z.string(),
    generations: z.number().int().nonnegative(),
    up: z.number().int().nonnegative(),
    down: z.number().int().nonnegative(),
    up_pct: decimal.nullable(),
    cost_usd: decimal,
    cost_per_reading: decimal.nullable(),
  })),
  verdicts: z.array(z.object({
    surface: z.string(),
    locale: z.string(),
    votes_ok: nullableBool,
    fallback_ok: nullableBool,
    stubs_ok: nullableBool,
    cost_ok: nullableBool,
  })),
  retention: z.array(z.object({
    locale: z.string(),
    text_mode: z.string(),
    cohort_size: z.number().int().nonnegative(),
    d7_eligible: z.number().int().nonnegative(),
    d7_retained: z.number().int().nonnegative(),
    d7_retention_pct: decimal.nullable(),
  })),
});
export type TextForms = z.infer<typeof TextFormsSchema>;

// DUE-412: share of synastry users who opened 2+ distinct partners.
export const SecondSynastrySchema = z.object({
  synastry_users: z.number().int().nonnegative(),
  second_synastry_users: z.number().int().nonnegative(),
  rate_pct: decimal.nullable(),
});
export type SecondSynastry = z.infer<typeof SecondSynastrySchema>;

const BASE = "/api/admin/analytics";

function windowQS(w: AnalyticsWindow): string {
  const qs = new URLSearchParams({ since: w.since, until: w.until });
  if (w.platform) qs.set("platform", w.platform);
  return qs.toString();
}

export function useFeatureUsage(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "feature-usage", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/feature-usage?${windowQS(w)}`,
        schema: FeatureUsageListSchema,
        signal,
      }),
  });
}

export function useTopFeatures(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "top-features", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/top-features?${windowQS(w)}`,
        schema: TopFeatureListSchema,
        signal,
      }),
  });
}

export function useActivity(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "activity", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/activity?${windowQS(w)}`,
        schema: ActivityResponseSchema,
        signal,
      }),
  });
}

export function useRetention(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "retention", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/retention?${windowQS(w)}`,
        schema: RetentionListSchema,
        signal,
      }),
  });
}

export function useFunnel(steps: string[], w: AnalyticsWindow) {
  const stepsParam = steps.join(",");
  return useQuery({
    queryKey: ["admin", "analytics", "funnel", stepsParam, w.since, w.until, w.platform],
    queryFn: ({ signal }) => {
      return request({
        path: `${BASE}/funnel?${windowQS(w)}&steps=${encodeURIComponent(stepsParam)}`,
        schema: FunnelListSchema,
        signal,
      });
    },
    enabled: steps.length >= 2,
  });
}

export function usePayerCorrelation(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "payer-correlation", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/payer-correlation?${windowQS(w)}`,
        schema: PayerCorrelationListSchema,
        signal,
      }),
  });
}

export function usePairSections(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "sections", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/sections?${qs.toString()}`,
        schema: PairSectionsSchema,
        signal,
      });
    },
  });
}

export function usePartnerDayLoop(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "partner-day-loop", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/partner-day-loop?${qs.toString()}`,
        schema: PartnerDayLoopSchema,
        signal,
      });
    },
  });
}

export function usePlatformRetention(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "platform-retention", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/platform-retention?${qs.toString()}`,
        schema: PlatformRetentionListSchema,
        signal,
      });
    },
  });
}

export function useWebAuthFunnel(w: AnalyticsWindow) {
  return useQuery({
    queryKey: ["admin", "analytics", "web-auth-funnel", w.since, w.until, w.platform],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/web-auth-funnel?${windowQS(w)}`,
        schema: WebAuthFunnelSchema,
        signal,
      }),
  });
}

export function useReadingDiversity(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "reading-diversity", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/reading-diversity?${qs.toString()}`,
        schema: ReadingDiversitySchema,
        signal,
      });
    },
  });
}

export function usePaidTextRules(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "paid-text-rules", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/paid-text-rules?${qs.toString()}`,
        schema: PaidTextRulesSchema,
        signal,
      });
    },
  });
}

export function useTextForms(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "text-forms", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/text-forms?${qs.toString()}`,
        schema: TextFormsSchema,
        signal,
      });
    },
  });
}

export function useSecondSynastry(w: Pick<AnalyticsWindow, "since" | "until">) {
  return useQuery({
    queryKey: ["admin", "analytics", "second-synastry", w.since, w.until],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ since: w.since, until: w.until });
      return request({
        path: `${BASE}/second-synastry?${qs.toString()}`,
        schema: SecondSynastrySchema,
        signal,
      });
    },
  });
}
